/**
 * @fileoverview Conflict-check vote bar (MOR-882)
 *
 * Part-to-whole of a small, known total (every member polled), so the form
 * is a single stacked bar rather than a chart with axes. The three segments
 * are states, not series, so they wear the app's reserved status colours —
 * the same red / green / grey the tally badges already use — and each one is
 * labelled underneath, so identity never rests on colour alone.
 *
 * "Hide no response" drops the outstanding segment and re-bases the bar on
 * the votes actually cast, which is how the result reads once a check has
 * been closed.
 */

import { CheckCircle2, ShieldAlert } from "lucide-react";

import { Switch } from "@/ui/primitives/switch";
import { Label } from "@/ui/primitives/label";
import { cn } from "@/utils/misc";

interface VoteSegment {
  key: "yes" | "no" | "pending";
  label: string;
  count: number;
  /** Fill + swatch classes drawn from the status palette. */
  fill: string;
  swatch: string;
}

export function ConflictVoteBar({
  yes,
  no,
  pending,
  hideNoResponse,
  onHideNoResponseChange,
  idPrefix,
}: {
  yes: number;
  no: number;
  pending: number;
  hideNoResponse: boolean;
  onHideNoResponseChange: (hide: boolean) => void;
  /** Unique per check so the toggle's label binds to its own switch. */
  idPrefix: string;
}) {
  const segments: VoteSegment[] = [
    {
      key: "yes",
      label: "Conflict",
      count: yes,
      fill: "bg-red-500 dark:bg-red-400",
      swatch: "bg-red-500 dark:bg-red-400",
    },
    {
      key: "no",
      label: "Clear",
      count: no,
      fill: "bg-green-600 dark:bg-green-400",
      swatch: "bg-green-600 dark:bg-green-400",
    },
    ...(hideNoResponse
      ? []
      : [
          {
            key: "pending" as const,
            label: "No response",
            count: pending,
            fill: "bg-muted-foreground/30",
            swatch: "bg-muted-foreground/30",
          },
        ]),
  ];

  const total = segments.reduce((sum, s) => sum + s.count, 0);
  const polled = yes + no + pending;
  const responded = yes + no;

  return (
    <div className="grid gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {hideNoResponse
            ? `${responded} of ${polled} responded`
            : `${polled} member${polled === 1 ? "" : "s"} polled`}
        </p>
        <div className="flex items-center gap-1.5">
          <Switch
            id={`${idPrefix}-hide-pending`}
            checked={hideNoResponse}
            onCheckedChange={onHideNoResponseChange}
          />
          <Label
            htmlFor={`${idPrefix}-hide-pending`}
            className="text-xs text-muted-foreground"
          >
            Hide no response
          </Label>
        </div>
      </div>

      {/* Stacked bar. 2px gaps between fills; rounded outer ends. */}
      {total > 0 ? (
        <div
          className="flex h-2.5 w-full gap-0.5 overflow-hidden"
          role="img"
          aria-label={`${yes} flagged a conflict, ${no} clear, ${pending} no response`}
        >
          {segments
            .filter((s) => s.count > 0)
            .map((s, index, visible) => (
              <div
                key={s.key}
                className={cn(
                  s.fill,
                  index === 0 && "rounded-l",
                  index === visible.length - 1 && "rounded-r",
                )}
                style={{ width: `${(s.count / total) * 100}%` }}
              />
            ))}
        </div>
      ) : (
        <div className="h-2.5 w-full rounded bg-muted" />
      )}

      {/* Legend — colour plus label plus value, in text ink. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        {segments.map((s) => (
          <span
            key={s.key}
            className="flex items-center gap-1.5 text-xs text-muted-foreground"
          >
            <span className={cn("size-2 rounded-sm", s.swatch)} />
            {s.key === "yes" && <ShieldAlert className="size-3 text-red-600" />}
            {s.key === "no" && (
              <CheckCircle2 className="size-3 text-green-600" />
            )}
            <span className="text-foreground">{s.label}</span>
            <span className="tabular-nums">
              {s.count}
              {total > 0 && ` (${Math.round((s.count / total) * 100)}%)`}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}
